import { selectPPTParcelAddress } from '@/app/modules/ppt/state/entities/parcel/ppt-parcel.selectors';
import { selectPPTNumberDisplayType } from '@/app/modules/ppt/state/ppt-state/ppt-state.selectors';
import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { NullLists } from '@shared/services/null-lists.service';
import { Viewport } from '@shared/services/viewport.service';
import { selectState } from '@/app/state/app-state/app-state.selectors';
import { State } from '@state/model';
import { PPTBill, PPTParcel, PPTParcelDetails } from 'egov-api';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'ppt-parcel-details',
    templateUrl: './parcel-details.component.html',
    styleUrls: ['./parcel-details.component.scss']
})
export class ParcelDetailsComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,


        // Template dependencies

        protected readonly nullLists: NullLists,
        protected readonly viewport: Viewport,
    )
    {}


    // Input properties

    @Input() protected set bill(value: PPTBill | undefined) { this.bill$.next(value); }
    protected readonly bill$ = new BehaviorSubject<PPTBill | undefined>(undefined);

    @Input() protected set parcel(value: PPTParcel | undefined) { this.parcel$.next(value); }
    protected readonly parcel$ = new BehaviorSubject<PPTParcel | undefined>(undefined);


    // Details

    protected readonly cityTaxSite$ = this.bill$.pipe(map(bill => bill?.cityTaxSite));
    protected readonly countyTaxSite$ = this.bill$.pipe(map(bill => bill?.countyTaxSite));

    protected readonly appraiserURL$ = this.parcel$.pipe(map(parcel => parcel?.appraiserURL));
    protected readonly parcelMapURL$ = this.parcel$.pipe(map(parcel => parcel?.parcelMapURL));

    private readonly fullOwner$ = this.parcel$.pipe(
        switchMap(parcel => parcel
            ? combineLatest([of(parcel), this.store.select(selectPPTParcelAddress(parcel.id))]).pipe(
                map(([parcel, address]) =>
                {
                    let result = '';

                    if (parcel.owner) result += parcel.owner + '\n';
                    if (address) result += address + '\n';

                    if (result.length)
                        return result.slice(0, -1);
                })
            )
            : of(undefined)
        )
    );

    protected readonly state$ = this.store.select(selectState);
    private readonly numberDisplayType$ = this.store.select(selectPPTNumberDisplayType);



    protected readonly parcelDetails$ = combineLatest([
        this.state$,
        this.parcel$,
        this.bill$,
        this.fullOwner$,
        this.numberDisplayType$
    ]).pipe(
        // eslint-disable-next-line complexity
        map(([state, parcel, bill, fullOwner, numberDisplayType]) =>
        {
            if (parcel?.satisfiesBillPage)
            {
                const data: { label: string, value: string }[] = [];

                if (state === 'fl')
                {
                    const details: PPTParcelDetails | undefined = parcel.details;

                    if (details)
                    {
                        if (bill?.billNumber) data.push({
                            label: 'Bill Number',
                            value: bill.billNumber,
                        });

                        if (details.propertyType) data.push({
                            label: 'Property Type',
                            value: details.propertyType,
                        });

                        if (fullOwner) data.push({
                            label: 'Owner',
                            value: fullOwner,
                        });

                        if (bill?.dba) data.push({
                            label: 'DBA',
                            value: bill.dba,
                        });

                        if (details.situs) data.push({
                            label: 'Situs',
                            value: details.situs,
                        });

                        if (details.legal) data.push({
                            label: 'Legal',
                            value: details.legal,
                        });

                        if (details.exemptionCodes?.length) data.push({
                            label: 'Exemption Codes',
                            value: details.exemptionCodes.join(', '),
                        });

                        if (details.escrowCode?.length) data.push({
                            label: 'Escrow Code',
                            value: details.escrowCode.join(', '),
                        });

                        if (details.landUseCode) data.push({
                            label: 'Land Use Code',
                            value: details.landUseCode,
                        });
                    }
                }
                else if (state === 'tn')
                {
                    if (numberDisplayType === 'Bill')
                    {
                        if (bill?.billNumber) data.push({
                            label: 'Bill Number',
                            value: bill.billNumber,
                        });
                    }
                    else if (numberDisplayType === 'Account')
                    {
                        if (bill) data.push({
                            label: 'Account Number',
                            value: bill.accountNumber,
                        });
                    }

                    if (bill?.parcelInformation?.propertyAddress) data.push({
                        label: 'Property Address',
                        value: bill.parcelInformation.propertyAddress,
                    });

                    if (bill?.parcelInformation?.ownerName) data.push({
                        label: 'Owner',
                        value: bill.parcelInformation.ownerName,
                    });

                    if (bill?.parcelInformation?.mailingAddress) data.push({
                        label: 'Mailing Address',
                        value: bill.parcelInformation.mailingAddress,
                    });

                    if (bill?.parcelInformation?.controlMap) data.push({
                        label: 'Control Map',
                        value: bill.parcelInformation.controlMap,
                    });

                    if (bill?.parcelInformation?.parcelGroup) data.push({
                        label: 'Group',
                        value: bill.parcelInformation.parcelGroup,
                    });

                    if (bill?.parcelInformation?.parcelNumber) data.push({
                        label: 'Parcel',
                        value: bill.parcelInformation.parcelNumber,
                    });

                    if (bill?.parcelInformation?.specialInterest) data.push({
                        label: 'S/I',
                        value: bill.parcelInformation.specialInterest,
                    });

                    if (bill?.parcelInformation?.cityNumber) data.push({
                        label: 'City Code',
                        value: bill.parcelInformation.cityNumber,
                    });
                }

                return data;
            }
            else
                return this.nullLists.get(8);
        })
    );
}
